import { AccordionContent, AccordionHeader, AccordionItem, AccordionTrigger, SidebarGroupLabel } from '@ibsys/kit';
import { MinusIcon, PlusIcon } from 'lucide-react';
import React, { FC, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsSidebarCollapsed } from '@/shared/hooks';
import { cn, RouteConfig } from '@/shared/lib';
import { SidebarNavItem, TruncatedText } from '@/shared/ui';
import { useIsGroupVisible } from '@/widgets/MainLayout/model/hooks/useIsGroupVisible';
import { useIsRouteVisible } from '@/widgets/MainLayout/model/hooks/useIsRouteVisible';
import { calcGroupName, collectLeafRoutes, isActiveNested } from '@/widgets/MainLayout/model/utils';
import { AppSidebarItem } from './AppSidebarItem';
import { AppSidebarLabel } from './AppSidebarLabel';
import { RouteNavIcon } from './RouteNavIcon';
import { SidebarFlyoutMenu } from './SidebarFlyoutMenu';

type Props = {
  route: RouteConfig;
  name: string;
  /** Корневой пункт меню (не вложенная подгруппа) — только для таких в свёрнутом виде показывается меню-подсказка */
  isRoot?: boolean;
};

export const AppSidebarGroup: FC<Props> = React.memo((props) => {
  const { route, name, isRoot } = props;
  const { pathname } = useLocation();
  const isCollapsed = useIsSidebarCollapsed();

  const [open, setOpen] = useState(() => isActiveNested(route, pathname));
  const isGroupActive = isActiveNested(route, pathname);

  // Меню-подсказка нужна не только при свёрнутой панели, но и когда сама группа схлопнута —
  // иначе непонятно, что скрыто внутри, пока не кликнешь.
  const showFlyout = isRoot && (isCollapsed || !open);
  // Пока содержимое группы скрыто (иконка в свёрнутой панели или схлопнутая группа),
  // единственный способ показать, что активная страница внутри — подсветить саму иконку.
  const highlightGroupIcon = isGroupActive && (isCollapsed || !open);

  const isGroupVisible = useIsGroupVisible();
  const isRouteVisible = useIsRouteVisible();

  // Единый источник динамических пунктов группы: используется и в развёрнутом списке
  // (AccordionContent), и в меню-подсказке свёрнутого сайдбара, чтобы оба состояния
  // показывали одни и те же пункты и не расходились между собой.
  const dynamicItems = route.navbar?.dynamicItems?.() ?? [];

  // Пункты подсказки отбираются тем же правилом видимости, что и пункты самого меню:
  // иначе подсказка предлагает закрытый пользователю раздел, а переход по нему приводит
  // на «Страница не найдена». Динамические пункты, подключённые к пункту, подсказка ставит
  // под ним сама — их хук зовёт строка пункта, а не перебор пунктов здесь.
  const flyoutRoutes = useMemo(
    () => (showFlyout ? collectLeafRoutes(route.children).filter(isRouteVisible) : []),
    [route.children, showFlyout, isRouteVisible],
  );

  if (!isGroupVisible(route)) {
    return null;
  }

  const groupLabel = (
    <SidebarGroupLabel
      className={cn(
        'uppercase p-2.5 flex items-center text-sidebar-foreground hover:text-sidebar-accent-foreground hover:bg-sidebar-accent cursor-pointer gap-2 w-full',
        isCollapsed && 'justify-center gap-0 px-0',
        highlightGroupIcon && 'bg-sidebar-accent text-sidebar-accent-foreground',
      )}
      onClick={() => setOpen((prev) => !prev)}
    >
      <RouteNavIcon route={route} />
      {!isCollapsed && (
        <>
          {/* Название раздела усекается по ширине меню, полное даёт подсказка: ширина меню
              принадлежит раскладке, и длинное название её не меняет. */}
          <TruncatedText>
            <AppSidebarLabel label={route.label} />
          </TruncatedText>
          <div className={'ml-auto'}>
            {!open ? <PlusIcon size={14} strokeWidth={1.25} /> : <MinusIcon size={14} strokeWidth={1.25} />}
          </div>
        </>
      )}
    </SidebarGroupLabel>
  );

  const header = route.label && (
    <AccordionHeader>
      <AccordionTrigger hideIcon className={'p-0'}>
        {groupLabel}
      </AccordionTrigger>
    </AccordionHeader>
  );

  return (
    <AccordionItem value={name} className={cn('flex flex-col overflow-hidden transition-[height] border-none p-0')}>
      {header &&
        (showFlyout ? (
          <SidebarFlyoutMenu title={<AppSidebarLabel label={route.label} />} routes={flyoutRoutes} items={dynamicItems}>
            {header}
          </SidebarFlyoutMenu>
        ) : (
          header
        ))}
      <AccordionContent
        className={cn(
          'flex flex-col gap-1 ps-[20px] py-1 relative show',
          'group-data-[state=collapsed]:ps-0',
          'group-data-[state=expanded]:before:absolute group-data-[state=expanded]:before:start-[15px] group-data-[state=expanded]:before:top-0 group-data-[state=expanded]:before:bottom-0 group-data-[state=expanded]:before:border-s group-data-[state=expanded]:before:border-border ',
          isCollapsed && 'hidden',
        )}
      >
        {route.children?.map((child, index) => {
          if (child.children?.length) {
            return <AppSidebarGroup key={child.path} route={child} name={calcGroupName(name, index)} />;
          }
          return !!child.navbar && <AppSidebarItem route={child} key={child.path} />;
        })}
        {/* Динамические пункты подменю группы — из того же dynamicItems, что и меню-подсказка */}
        {dynamicItems.map((item) => (
          <SidebarNavItem key={item.key} path={item.path} icon={item.icon} label={item.label} />
        ))}
      </AccordionContent>
    </AccordionItem>
  );
});
